import React from "react";
import * as ReactAriaPrimitive from "react-aria-components";

import { cn } from "~/components/ui/cn";

const Combobox = <T extends object>({
  ...props
}: ReactAriaPrimitive.ComboBoxProps<T>) => (
  // allowsEmptyCollection is needed to prevent the no results message from showing
  <ReactAriaPrimitive.ComboBox allowsEmptyCollection {...props} />
);

const ComboboxInput: React.FC<
  React.ComponentProps<typeof ReactAriaPrimitive.Input>
> = ({ className, disabled, ...props }) => (
  <ReactAriaPrimitive.Input
    className={cn(
      "form-input block w-full rounded border p-2 text-sm leading-5.5 outline-none",
      {
        "text-gray-900 placeholder-gray-500 transition duration-300": !disabled,
        "cursor-not-allowed border-gray-100 bg-gray-50 text-gray-500 placeholder-gray-300":
          disabled,
        'border-gray-500 hover:border-gray-700 focus:border-gray-700 focus:ring-gray-700 aria-[invalid="true"]:border-red-600 aria-[invalid="true"]:ring-red-600':
          !disabled,
      },
      className,
    )}
    disabled={disabled}
    {...props}
  />
);

ComboboxInput.displayName = "ComboboxInput";

const ComboboxPopover = ({
  className,
  ...props
}: ReactAriaPrimitive.PopoverProps) => (
  <ReactAriaPrimitive.Popover
    className={cn(
      "z-10 w-[--trigger-width] overflow-auto rounded-md border bg-white p-1 shadow-lg",
      className,
    )}
    {...props}
  />
);

const ComboboxList = <T extends object>({
  className,
  ...props
}: ReactAriaPrimitive.ListBoxProps<T>) => (
  <ReactAriaPrimitive.ListBox className={className} {...props} />
);

const ComboboxItem = ({
  className,
  ...props
}: ReactAriaPrimitive.ListBoxItemProps) => (
  <ReactAriaPrimitive.ListBoxItem
    className={cn(
      "flex cursor-pointer items-center whitespace-pre rounded-sm p-2 text-sm text-gray-700 data-[focused=true]:bg-green-300 data-[hovered=true]:bg-green-300 data-[hovered=true]:text-gray-900",
      className,
    )}
    {...props}
  />
);

const ComboboxSection = <T extends object>({
  className,
  ...props
}: ReactAriaPrimitive.SectionProps<T>) => (
  <ReactAriaPrimitive.ListBoxSection className={className} {...props} />
);

const ComboboxHeader = ({
  className,
  ...props
}: React.ComponentProps<"div">) => (
  <ReactAriaPrimitive.Header
    className={cn(
      "px-2 py-1 text-xs font-semibold uppercase tracking-wider text-gray-700",
      className,
    )}
    {...props}
  />
);

const ComboboxEmpty = ({
  className,
  ...props
}: React.ComponentProps<"div">) => (
  <div
    className={cn("flex items-center p-2 text-sm text-gray-900", className)}
    {...props}
  />
);

export {
  Combobox,
  ComboboxEmpty,
  ComboboxHeader,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxPopover,
  ComboboxSection,
};
